import React, { useMemo } from "react";

import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { RestartAlt } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";

import { Site, SiteScore } from "@/types";

interface SiteSearchCardProps {
  siteData: SiteScore[];
  filteredSites: Site[];
  setSites: (sites: number[]) => void;
  selectedSites: number[];
  siteType: string;
}

const SiteSearchCard: React.FC<SiteSearchCardProps> = ({
  siteData,
  filteredSites,
  setSites,
  selectedSites,
  siteType
}) => {
  const theme = useTheme();

  const filterByType = (s: SiteScore[]) => {
    // if (siteType === 'ALL') return s;
    // return s.filter(site => site.site_type === siteType);
    return s
  }

  return (
    <Card
      sx={{
        mt: 2,
        borderRadius: 1,
        borderColor: theme.palette.divider,
        width: "100%",
        position: "relative",
        bgColor: theme.palette.background.paper
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          pb: 2,
          "&:last-child": { pb: 2 },
          gap: 1
        }}
      >
        <Box width="100%" display="flex" flexDirection="row" gap={1}>
          <Autocomplete
            multiple
            fullWidth
            disableCloseOnSelect
            disableClearable
            options={siteData}
            value={filteredSites}
            getOptionLabel={(option) => option.site_name}
            isOptionEqualToValue={(option, value) => option.site_id === value.site_id}
            filterOptions={(options, state) => {
              const selectedSet = new Set(selectedSites);
              const unselected = options.filter((site) => !selectedSet.has(site.site_id));

              // Also apply standard search text matching
              if (!state.inputValue) return unselected;
              const search = state.inputValue.toLowerCase();
              return unselected.filter((site) =>
                site.site_name.toLowerCase().includes(search)
              );
            }}

            onChange={(_, newValue) => {
              const updatedIds = newValue.map((site) => site.site_id);
              setSites(updatedIds);
            }}


            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Search & Add Sites"
                variant="outlined"
                color="secondary"
                size="small"
              />
            )}

            renderTags={() => null}
          />
          {selectedSites.length > 0 && (
            <HelpBox title="Clear all selected sites">
              <IconButton
                size="small"
                onClick={() => setSites([])}
              >
                <RestartAlt fontSize="small" />
              </IconButton>
            </HelpBox>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption">
            Quick Select
          </Typography>
          <Chip
            label='Top 20'
            size='small'
            variant='filled'
            color='secondary'
            onClick={() => setSites(filterByType(siteData).slice(0, 20).map((site) => site.site_id))}
          />
          <Chip
            label='Over Indexing'
            size='small'
            variant='filled'
            color='secondary'
            onClick={() => setSites(filterByType(siteData).filter(x => x.score > 1).map(x => x.site_id))}
          />
          <Chip
            label='All Sites'
            size='small'
            variant='filled'
            color='secondary'
            onClick={() => setSites(filterByType(siteData).map(x => x.site_id))}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default SiteSearchCard;
